// src/components/ChartOverlayController.jsx

import { useEffect, useLayoutEffect, useRef } from "react";
import {
  drawStructureOverlays,
  resetOverlays,
  syncOverlayLabels,
} from "../utils/chartOverlayEngine";
import { detectSupplyDemandZones } from "../utils/engine/zoneEngine";

function buildDisplayLabel(zone, state) {
  const zoneName =
    zone.zoneName || (zone.isDemand ? "demand" : "supply");
  const prefix =
    state === "failed" ? "Failed" : state === "tested" ? "Tested" : "Fresh";
  const side = zoneName === "demand" ? "Demand" : zoneName === "supply" ? "Supply" : zone.name || "Zone";

  if (zoneName !== "demand" && zoneName !== "supply") {
    return zone.label || zone.name || zone.zoneLabel || "Zone";
  }

  return `${prefix} ${side}`;
}

function resolveDisplayZone(zone, zoneOutcome, activeZoneId) {
  if (!zone) {
    return zone;
  }

  const trackedZoneMatches =
    zoneOutcome?.zoneId &&
    zone.id === zoneOutcome.zoneId;
  const trackedOutcomeFailed =
    trackedZoneMatches &&
    (zoneOutcome.state === "FAILED" || zoneOutcome.result?.result === "FAIL");

  const baseState = zone.failed
    ? "failed"
    : zone.tested || zone.mitigated || zone.state === "tested"
    ? "tested"
    : zone.state === "failed"
    ? "failed"
    : zone.state === "fresh"
    ? "fresh"
    : zone.state || "fresh";
  const nextState = trackedOutcomeFailed ? "failed" : baseState;

  return {
    ...zone,
    state: nextState,
    failed: trackedOutcomeFailed ? true : zone.failed === true || nextState === "failed",
    valid: trackedOutcomeFailed ? false : zone.valid,
    outcomeState: trackedZoneMatches ? zoneOutcome.state : zone.outcomeState || null,
    outcomeResult: trackedZoneMatches
      ? zoneOutcome.result?.result || null
      : zone.outcomeResult || null,
    label: trackedZoneMatches
      ? zoneOutcome.label || buildDisplayLabel(zone, nextState)
      : buildDisplayLabel(zone, nextState),
    isHighlighted: activeZoneId != null && zone.id === activeZoneId,
  };
}

function dedupeZones(zones = [], timeframe) {
  return zones.reduce((uniqueZones, zone) => {
    const existingIndex = uniqueZones.findIndex((item) => {
      const sameType = item.type === zone.type;
      const sameState = item.state === zone.state;
      const lowDelta = Math.abs((item.low ?? 0) - (zone.low ?? 0));
      const highDelta = Math.abs((item.high ?? 0) - (zone.high ?? 0));
      const timeDelta = Math.abs((item.breakoutTime ?? 0) - (zone.breakoutTime ?? 0));

      return (
        sameType &&
        sameState &&
        lowDelta <= 0.01 &&
        highDelta <= 0.01 &&
        timeDelta <= timeframe * 2
      );
    });

    if (existingIndex === -1) {
      uniqueZones.push(zone);
      return uniqueZones;
    }

    if (
      (zone.impulseDistance ?? 0) >
      (uniqueZones[existingIndex].impulseDistance ?? 0)
    ) {
      uniqueZones[existingIndex] = zone;
    }

    return uniqueZones;
  }, []);
}

function getZoneSortTime(zone) {
  return (
    zone?.breakoutTime ??
    zone?.startTime ??
    zone?.createdAt ??
    zone?.time ??
    0
  );
}

function getFreshZones(zones = [], limit = 5) {
  return zones
    .filter((zone) => zone?.state === "fresh")
    .sort((left, right) => getZoneSortTime(right) - getZoneSortTime(left))
    .slice(0, limit);
}

export default function ChartOverlayController({
  chartApi,
  analysis,
  structure,
  candles,
  symbol,
  timeframe,
  selectedZoneId = "ALL",
  selectedOrderBlockId = "ALL",
  onZonesChange,
  onOrderBlocksChange,
  onZoneFilterReset,
  onOrderBlockFilterReset,
  showAllZones = false,
}) {
  const market = symbol?.includes("Crash") ? "CRASH" : "BOOM";
  const lastDrawSignatureRef = useRef("");
  const latestRenderRef = useRef({
    structure: null,
    candles: [],
    timeframe,
    zones: [],
  });

  function toTitleCase(value = "") {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  function buildDrawSignature(nextStructure, nextZones, nextTimeframe) {
    const structureSignature = nextStructure?.event
      ? [
          nextStructure.event,
          nextStructure.direction,
          nextStructure.bosCandleTime,
          nextStructure.swingIndex,
        ].join(":")
      : "no-structure";

    const zoneSignature = nextZones
      .map((zone) =>
        [
          zone.id,
          zone.state,
          zone.outcomeState,
          zone.outcomeResult,
          zone.label,
          zone.startTime,
          zone.breakoutTime,
          zone.high,
          zone.low,
          zone.isHighlighted,
        ].join(":")
      )
      .join("|");

    return `${nextTimeframe}::${structureSignature}::${zoneSignature}`;
  }

  // RESET overlays when symbol / timeframe changes
  useLayoutEffect(() => {
    if (!chartApi) return;
    lastDrawSignatureRef.current = "";
    latestRenderRef.current = {
      structure: null,
      candles: [],
      timeframe,
      zones: [],
    };
    onZoneFilterReset?.("ALL");
    onOrderBlockFilterReset?.("ALL");
    resetOverlays(chartApi);
  }, [
    chartApi,
    symbol,
    timeframe,
    onZoneFilterReset,
    onOrderBlockFilterReset,
  ]);

  // DRAW overlays only for active structure; otherwise clear stale drawings
  useLayoutEffect(() => {
    if (!chartApi) return;

    const zoneAnalysis = analysis?.zoneAnalysis || detectSupplyDemandZones(candles, market);
    const zoneOutcome = analysis?.zoneOutcome || null;
    const activeZone = analysis?.bosState?.zone || analysis?.zoneState || null;
    const activeZoneId = activeZone?.id ?? null;
    const rawZones = showAllZones
      ? [
          ...(zoneAnalysis.demandZones || []),
          ...(zoneAnalysis.supplyZones || []),
          ...(zoneAnalysis.hiddenZones || []),
        ]
      : zoneAnalysis.zones;
    const displayZones = rawZones.map((zone) =>
      resolveDisplayZone(zone, zoneOutcome, activeZoneId)
    );
    const dedupedZones = dedupeZones(displayZones, timeframe);
    const freshZones = getFreshZones(dedupedZones, 5);
    const zoneGroups = freshZones.reduce((groups, zone) => {
      const filterId = `${zone.type}-${zone.state}`.toUpperCase();
      const existingGroup = groups.find((group) => group.id === filterId);

      if (existingGroup) {
        existingGroup.count += 1;
        return groups;
      }

      groups.push({
        id: filterId,
        label: zone.label || `${toTitleCase(zone.state)} ${toTitleCase(zone.name)} Zone`,
        count: 1,
      });

      return groups;
    }, []);
    const zoneOptions = zoneGroups.map((group) => ({
      id: group.id,
      label: `${group.label} (${group.count})`,
    }));
    const visibleZones =
      selectedZoneId === "ALL"
        ? freshZones
        : freshZones.filter(
            (zone) =>
              `${zone.type}-${zone.state}`.toUpperCase() === selectedZoneId
          );
    const hasStructure = Boolean(structure?.event);
    const hasZones = visibleZones.length > 0;

    onZonesChange?.(zoneOptions);
    onOrderBlocksChange?.([]);
    latestRenderRef.current = {
      structure,
      candles,
      timeframe,
      zones: visibleZones,
    };

    if (!hasStructure && !hasZones) {
      lastDrawSignatureRef.current = "";
      latestRenderRef.current = {
        structure: null,
        candles: [],
        timeframe,
        zones: [],
      };
      resetOverlays(chartApi);
      return;
    }

    const nextSignature = buildDrawSignature(
      structure,
      visibleZones,
      timeframe
    );

    if (lastDrawSignatureRef.current !== nextSignature) {
      drawStructureOverlays(chartApi, structure, candles, {
        zones: visibleZones,
        timeframe,
      });
      lastDrawSignatureRef.current = nextSignature;
      return;
    }

    syncOverlayLabels(
      chartApi,
      structure,
      candles,
      visibleZones,
      timeframe,
      candles[candles.length - 1]?.time ?? null
    );
  }, [
    chartApi,
    analysis,
    structure,
    candles,
    timeframe,
    market,
    selectedZoneId,
    selectedOrderBlockId,
    onZonesChange,
    onOrderBlocksChange,
    showAllZones,
  ]);

  useEffect(() => {
    if (!chartApi?.chart || !chartApi?.container) {
      return undefined;
    }

    const redrawLabels = () => {
      syncOverlayLabels(
        chartApi,
        latestRenderRef.current.structure,
        latestRenderRef.current.candles,
        latestRenderRef.current.zones,
        latestRenderRef.current.timeframe,
        latestRenderRef.current.candles[
          latestRenderRef.current.candles.length - 1
        ]?.time ?? null
      );
    };

    const timeScale = chartApi.chart.timeScale();
    timeScale.subscribeVisibleLogicalRangeChange(redrawLabels);

    const resizeObserver = new ResizeObserver(() => {
      redrawLabels();
    });

    resizeObserver.observe(chartApi.container);
    redrawLabels();

    return () => {
      timeScale.unsubscribeVisibleLogicalRangeChange(redrawLabels);
      resizeObserver.disconnect();
      resetOverlays(chartApi);
    };
  }, [chartApi]);

  return null;
}
